"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import clsx from "clsx";

type FadeInCallback = (isIntersecting: boolean) => void;

let sharedObserver: IntersectionObserver | null = null;
const observedElements = new Map<Element, FadeInCallback>();

function getSharedObserver(): IntersectionObserver {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const cb = observedElements.get(entry.target);
          if (cb) {
            cb(entry.isIntersecting);
          }
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
  }
  return sharedObserver;
}

function observeElement(el: Element, callback: FadeInCallback) {
  observedElements.set(el, callback);
  getSharedObserver().observe(el);
}

function unobserveElement(el: Element) {
  observedElements.delete(el);
  if (sharedObserver) {
    sharedObserver.unobserve(el);
    if (observedElements.size === 0) {
      sharedObserver.disconnect();
      sharedObserver = null;
    }
  }
}

let motionListenerCount = 0;
let motionCallbacks = new Set<() => void>();
let motionCleanup: (() => void) | null = null;

function subscribeMotionChange(cb: () => void) {
  motionCallbacks.add(cb);
  motionListenerCount++;

  if (motionListenerCount === 1) {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handler = () => {
      for (const fn of motionCallbacks) fn();
    };

    mediaQuery.addEventListener("change", handler);

    const mutObs = new MutationObserver(handler);
    mutObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-reduce-motion"],
    });

    motionCleanup = () => {
      mediaQuery.removeEventListener("change", handler);
      mutObs.disconnect();
    };
  }

  return () => {
    motionCallbacks.delete(cb);
    motionListenerCount--;
    if (motionListenerCount === 0 && motionCleanup) {
      motionCleanup();
      motionCleanup = null;
    }
  };
}

function isReducedMotion(): boolean {
  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const userPref = document.documentElement.getAttribute("data-reduce-motion") === "true";
  return mediaQuery.matches || userPref;
}

interface FadeInProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  duration?: number;
}

export default function FadeIn({
  children,
  className,
  delay = 0,
  direction = "up",
  duration = 600,
}: FadeInProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const showImmediately = useCallback(() => {
    setIsVisible(true);
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect -- syncing with external systems (matchMedia, IntersectionObserver) */
  useEffect(() => {
    if (isReducedMotion()) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) {
      setIsVisible(true);
      return;
    }

    observeElement(el, (intersecting) => {
      if (intersecting) {
        setIsVisible(true);
        unobserveElement(el);
      }
    });

    const unsubMotion = subscribeMotionChange(() => {
      if (isReducedMotion()) {
        showImmediately();
        unobserveElement(el);
      }
    });

    return () => {
      unobserveElement(el);
      unsubMotion();
    };
  }, [showImmediately]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const directionStyles = {
    up: "translate-y-8",
    down: "-translate-y-8",
    left: "translate-x-8",
    right: "-translate-x-8",
    none: "",
  };

  return (
    <div
      ref={ref}
      className={clsx(
        "transition-all ease-out",
        isVisible ? "opacity-100 translate-x-0 translate-y-0" : `opacity-0 ${directionStyles[direction]}`,
        className
      )}
      style={{ transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}
